<template>
    <div>
        <div class="border-t border-gray-200 mt-6 pt-6">
            <div class="flex items-center justify-between cursor-pointer mb-4 group" @click="isMxOpen = !isMxOpen">
                <h3 class="text-lg font-medium">MX Connection Data</h3>
                <i class="fas fa-chevron-down transition-transform duration-200 text-gray-400 group-hover:text-gray-600"
                   :class="{ 'transform rotate-180': isMxOpen }"></i>
            </div>
            
            <div v-show="isMxOpen" class="transition-all duration-200">
                <div class="space-y-8">
                    <ul class="space-y-8">
                        <li
                            class="bg-white p-4 rounded-lg border border-gray-200"
                            v-for="(member, mIndex) in members"
                            :key="'member-' + mIndex"
                        >
                            <div class="mb-2">
                                {{ member.name }}
                                <span class="text-sm">
                                    ({{ member.institution_code }}) -
                                    <span
                                        class="rounded p-1 px-2 text-white text-xs"
                                        :class="{
                                            'bg-amber-700': member.connection_status !== 'CONNECTED',
                                            'bg-gray-700': member.connection_status === 'CONNECTED',
                                        }"
                                    >
                                        {{ member.connection_status }}
                                    </span>
                                </span>
                            </div>
                            <div class="mb-2 text-sm text-gray-600">
                                <div class="flex items-center gap-2">
                                    Member GUID: {{ member.guid }}
                                    <button 
                                        @click="copyGuid(member)"
                                        class="text-sm px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded flex items-center gap-1 transition-all duration-200"
                                        :class="{ 'bg-green-100 text-green-600': member.guidCopied }"
                                    >
                                        <svg v-if="!member.guidCopied" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                        <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                        </svg>
                                        {{ member.guidCopied ? 'Copied!' : 'Copy' }}
                                    </button>
                                </div>
                                <div v-if="member.connect_attempt">User GUID: {{ member.connect_attempt.user_guid }}</div>
                            </div>
                            <div class="grid gap-4 grid-cols-12">
                                <div class="col-span-3">
                                    <ActionButton
                                        v-if="member.connect_attempt"
                                        @clicked="downloadAccounts(member)"
                                        label="Force Download Accounts"
                                        :loading="member.accountsDownloading"
                                        classes="btn btn-secondary btn-sm"
                                    />
                                </div>
                                <div class="col-span-3">
                                    <ActionButton
                                        v-if="member.connect_attempt"
                                        @clicked="refetch(member)"
                                        label="Refetch Member Rewards"
                                        :loading="member.accountsDownloading"
                                        classes="btn btn-secondary btn-sm"
                                    />
                                </div>
                                <div class="col-span-3">
                                    <ActionButton
                                        v-if="member.connect_attempt"
                                        @clicked="viewRewardsData(member)"
                                        label="View Rewards Data"
                                        :loading="member.loadingRewardsData"
                                        classes="btn btn-secondary btn-sm"
                                    />
                                </div>
                                <div class="col-span-3">
                                    <ActionButton
                                        v-if="member.guid"
                                        @clicked="viewAccountData(member)"
                                        label="View Account Data"
                                        :loading="member.loadingAccountData"
                                        classes="btn btn-secondary btn-sm"
                                    />
                                </div>
                            </div>
                            <div v-if="member.rewardsData" class="mt-4 bg-gray-900 p-4 rounded relative font-mono">
                                <div class="absolute top-2 right-2 flex items-center gap-2">
                                    <button 
                                        @click="closeRewardsData(member)"
                                        class="text-sm px-2 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded flex items-center gap-1 transition-all duration-200"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                        Close
                                    </button>
                                    <button 
                                        @click="copyToClipboard(member)"
                                        class="text-sm px-2 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded flex items-center gap-1 transition-all duration-200"
                                        :class="{ 'bg-green-600': member.copied }"
                                    >
                                        <svg v-if="!member.copied" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                        <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                        </svg>
                                        {{ member.copied ? 'Copied!' : 'Copy' }}
                                    </button>
                                </div>
                                <pre class="text-sm overflow-auto max-h-[800px] text-white"><code v-html="formatJson(member.rewardsData)"></code></pre>
                            </div>
                            <div v-if="member.accountData" class="mt-4 bg-gray-900 p-4 rounded relative font-mono">
                                <div class="absolute top-2 right-2 flex items-center gap-2">
                                    <button 
                                        @click="closeAccountData(member)"
                                        class="text-sm px-2 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded flex items-center gap-1 transition-all duration-200"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                        Close
                                    </button>
                                    <button 
                                        @click="copyAccountData(member)"
                                        class="text-sm px-2 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded flex items-center gap-1 transition-all duration-200"
                                        :class="{ 'bg-green-600': member.accountDataCopied }"
                                    >
                                        <svg v-if="!member.accountDataCopied" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                        <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                        </svg>
                                        {{ member.accountDataCopied ? 'Copied!' : 'Copy' }}
                                    </button>
                                </div>
                                <pre class="text-sm overflow-auto max-h-[800px] text-white"><code v-html="formatJson(member.accountData)"></code></pre>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ActionButton from '@/components/action-button.vue'

export default {
    name: 'UserAdminActions',
    components: {
        ActionButton,
    },
    props: {
        userid: {
            type: Number,
            default: 0,
        },
    },
    data: () => ({
        members: [],
        accounts: [],
        fetchedMember: {},
        isMxOpen: false
    }),
    mounted() {
        this.getMembers()
    },
    methods: {
        formatJson(data) {
            const jsonString = JSON.stringify(data, null, 2);
            return jsonString
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                // Match and color dates in orange
                .replace(/: "(\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2})"/g, ': <span class="text-orange-400">"$1"</span>')
                // Match and color balance values in blue (including quotes)
                .replace(/("balance":\s*)"(\d+)"/g, '$1<span class="text-blue-400">"$2"</span>')
                // Match and color other string values in green
                .replace(/: "([^"]+)"/g, ': <span class="text-green-400">"$1"</span>')
                // Match and color numbers (including current_rewards_balance)
                .replace(/: (\d+)/g, ': <span class="text-blue-400">$1</span>')
                // Match and color booleans and null
                .replace(/: (true|false|null)/g, ': <span class="text-yellow-400">$1</span>');
        },
        closeRewardsData(member) {
            member.rewardsData = null;
        },
        copyToClipboard(member) {
            const jsonString = JSON.stringify(member.rewardsData, null, 2);
            
            navigator.clipboard.writeText(jsonString).then(() => {
                member.copied = true;
                
                setTimeout(() => {
                    member.copied = false;
                }, 2000);
            });
        },
        closeAccountData(member) {
            member.accountData = null;
            member.accountDataCopied = false;
        },
        copyAccountData(member) {
            const jsonString = JSON.stringify(member.accountData, null, 2);
            
            navigator.clipboard.writeText(jsonString).then(() => {
                member.accountDataCopied = true;
                
                setTimeout(() => {
                    member.accountDataCopied = false;
                }, 2000);
            });
        },
        copyGuid(member) {
            navigator.clipboard.writeText(member.guid).then(() => {
                member.guidCopied = true;
                setTimeout(() => {
                    member.guidCopied = false;
                }, 2000);
            });
        },
        getMembers() {
            ApiService.apiCall('/members', 'post', {
                from_admin: true,
                user_id: this.userid,
            })
                .then((response) => {
                    this.members = response.data.map((member) => ({
                        ...member,
                        rewardsData: null,
                        loadingRewardsData: false,
                        accountsDownloading: false,
                        accountData: null,
                        loadingAccountData: false,
                        accountDataCopied: false,
                        copied: false,
                        guidCopied: false
                    }));
                })
                .catch((response) => {
                    this.$root.handleHttpStatus(response);
                });
        },
        downloadAccounts(member) {
            member.accountsDownloading = true;
            ApiService.apiCall(`/admin/users/${this.userid}/force-download/${member.guid}`, 'post', {}, {
                headers: {
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
                }
            })
                .then((response) => {
                    member.accountsDownloading = false;
                    this.$notify(
                        {
                            group: 'success',
                            title: 'Account Download',
                            text: response.data.message,
                        },
                        4000
                    );
                })
                .catch((response) => {
                    member.accountsDownloading = false;
                    this.$root.handleHttpStatus(response);
                });
        },
        refetch(member) {
            member.accountsDownloading = true;
            ApiService.apiCall(`/admin/users/${this.userid}/refetch-rewards/${member.guid}`, 'post', {}, {
                headers: {
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
                }
            })
                .then((response) => {
                    member.accountsDownloading = false;
                    this.$notify(
                        {
                            group: 'success',
                            title: 'Rewards Refresh',
                            text: response.data.message,
                        },
                        4000
                    );
                })
                .catch((response) => {
                    member.accountsDownloading = false;
                    this.$root.handleHttpStatus(response);
                });
        },
        viewRewardsData(member) {
            member.loadingRewardsData = true;
            ApiService.apiCall(`/admin/users/${this.userid}/rewards/${member.guid}`, 'get')
                .then((response) => {
                    member.rewardsData = response.data;
                    member.loadingRewardsData = false;
                })
                .catch((response) => {
                    member.loadingRewardsData = false;
                    this.$root.handleHttpStatus(response);
                });
        },
        viewAccountData(member) {
            member.loadingAccountData = true;
            ApiService.apiCall(`/admin/users/${this.userid}/members/${member.guid}/accounts`, 'get')
                .then((response) => {
                    member.accountData = response.data;
                    member.loadingAccountData = false;
                })
                .catch((response) => {
                    member.loadingAccountData = false;
                    this.$root.handleHttpStatus(response);
                });
        },
    },
};
</script>
